import {
  createActionTypes,
  createAsyncAction,
  createAsyncActionTypes,
} from "@dpdgroupuk/redux-action-creator";
import {
  initialize,
  getFormInitialValues,
  SubmissionError,
  getFormValues,
  stopAsyncValidation,
  change,
  touch,
} from "redux-form";
import { fileTypeFromBlob } from "file-type";
import {
  F,
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_IMAGE_FILE_EXTENSIONS,
} from "../../constants";
import { userApis } from "../../apis";
import * as PaperlessSelectors from "./selectors";

export const PaperlessActionTypes = createActionTypes("USER", {
  LOAD_PAPERLESS_SETTINGS: createAsyncActionTypes("LOAD_PAPERLESS_SETTINGS"),
  SAVE_PAPERLESS_SETTINGS: createAsyncActionTypes("SAVE_PAPERLESS_SETTINGS"),
  FETCH_PAPERLESS_SETTINGS_USER: createAsyncActionTypes(
    "FETCH_PAPERLESS_SETTINGS_USER"
  ),
  FETCH_ACCOUNT_PAPERLESS_SETTINGS_USER: createAsyncActionTypes(
    "FETCH_ACCOUNT_PAPERLESS_SETTINGS_USER"
  ),
  POST_PAPERLESS_TRADE_ACCOUNT_SETTINGS: createAsyncActionTypes(
    "POST_PAPERLESS_TRADE_ACCOUNT_SETTINGS"
  ),
  DISAGREE_TERMS_AND_CONDITIONS: createAsyncActionTypes(
    "DISAGREE_TERMS_AND_CONDITIONS"
  ),
  PAPERLESS_TRADE_OPTIONS_OUT: createAsyncActionTypes(
    "PAPERLESS_TRADE_OPTIONS_OUT"
  ),
});

export const fetchPaperlessSettingsUser = createAsyncAction(
  userApis.getPaperlessTradeSettings,
  PaperlessActionTypes.FETCH_PAPERLESS_SETTINGS_USER
);

export const getAccountPaperlessTradeSettings = createAsyncAction(
  userApis.getAccountPaperlessTradeSettings,
  PaperlessActionTypes.FETCH_ACCOUNT_PAPERLESS_SETTINGS_USER
);

export const postPaperlessTradeAccountSettings = createAsyncAction(
  userApis.postPaperlessTradeAccountSettings,
  PaperlessActionTypes.POST_PAPERLESS_TRADE_ACCOUNT_SETTINGS
);

export const setPaperlessTradeOptionsOut = createAsyncAction(
  userApis.postPaperlessTradeOptionsOut,
  PaperlessActionTypes.PAPERLESS_TRADE_OPTIONS_OUT
);

const getSignatureImage = signatureImageKey =>
  signatureImageKey && [
    {
      name: F.PAPERLESS_SETTINGS.SIGNATURE_IMAGE_DEFAULT_NAME,
      originName: signatureImageKey,
    },
  ];

const getLogoImage = logoImageKey =>
  logoImageKey && [
    {
      name: F.PAPERLESS_SETTINGS.LOGO_IMAGE_DEFAULT_NAME,
      originName: logoImageKey,
    },
  ];

export const changeAccount = accountNumber => async (dispatch, getState) => {
  const state = getState();
  const accounts = PaperlessSelectors.getPaperlessAccounts(state);
  const account = accounts.find(
    account => account.accountNumber === accountNumber
  );
  const accountSettings = await dispatch(
    getAccountPaperlessTradeSettings(accountNumber, account.paperlessDate)
  );

  const formState = {
    agree: true,
    account: accountNumber,
    name: accountSettings.data.printedName,
    title: accountSettings.data.title,
    signatureImageKey: accountSettings.data.signatureImageKey,
    signature: getSignatureImage(accountSettings.data.signatureImageKey),
    logoImageKey: accountSettings.data.logoImageKey,
    logo: getLogoImage(accountSettings.data.logoImageKey),
    signatureKey: accountSettings.data.signatureImageKey,
    logoKey: accountSettings.data.logoImageKey,
    allAccounts: false,
  };

  dispatch(
    initialize(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM, formState, true, {
      keepDirty: true,
      keepValues: true,
    })
  );

  return formState;
};

export const loadPaperlessSettings = createAsyncAction(
  () => async dispatch => {
    const settings = await dispatch(fetchPaperlessSettingsUser());
    let formValues = {
      agree: settings.data?.some(
        accountSetting => accountSetting.paperlessState
      ),
    };

    if (settings.data.length === 1 && formValues.agree) {
      const { accountNumber, paperlessDate } = settings.data[0];
      const formState = await dispatch(
        changeAccount(accountNumber, paperlessDate)
      );

      formValues = { ...formValues, ...formState };
    }

    return {
      data: formValues,
    };
  },
  PaperlessActionTypes.LOAD_PAPERLESS_SETTINGS
);

const convertByteToMB = size => {
  const sizeInMB = Math.floor(size / (1024 * 1024));

  return Math.max(sizeInMB, 1);
};

const validateFileSize = file =>
  file &&
  file.size > F.PAPERLESS_SETTINGS.FILE_SIZE_RESTRICTION &&
  `File too large (max ${convertByteToMB(
    F.PAPERLESS_SETTINGS.FILE_SIZE_RESTRICTION
  )}MB)`;

const validateFileTypeImage = async (
  file,
  {
    allowedMIMETypes = ALLOWED_IMAGE_MIME_TYPES,
    allowedExtensions = ALLOWED_IMAGE_FILE_EXTENSIONS,
  } = {}
) => {
  const image = await getRenderableImage(file);

  if (!image) {
    return `Invalid file type (${allowedExtensions} only)`;
  }

  const newFile = new File([file], file.name, { type: "" });
  const streamWithFileType = await fileTypeFromBlob(newFile);

  return (
    allowedMIMETypes.indexOf(streamWithFileType?.mime) === -1 &&
    `Invalid file type (${allowedExtensions} only)`
  );
};

const getRenderableImage = file => {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(false);
    img.src = URL.createObjectURL(file);
  });
};

const validateFileDimensions = async file => {
  try {
    const image = await getRenderableImage(file);
    const dimensions = image
      ? { width: image.width, height: image.height }
      : { width: 0, height: 0 };

    return (
      (dimensions.width > 8000 || dimensions.height > 2000) &&
      "Image dimensions too large (max 8000x2000px)"
    );
  } catch (error) {
    return false;
  }
};

export const validateImageFile = async (file, options) => {
  try {
    const validations = [
      validateFileSize,
      validateFileTypeImage,
      validateFileDimensions,
    ];

    for (let validateFn of validations) {
      const error = await validateFn(file, options);

      if (error) {
        return error;
      }
    }
  } catch (error) {
    console.error("Error validating image file:", error);
    return "Image cannot be validated";
  }
};

export const validateImagePaperlessForm =
  (fieldName, files) => async (dispatch, getState) => {
    if (files?.length) {
      await dispatch(
        change(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM, fieldName, files)
      );
      await dispatch(forceAsyncFomValidation());
    }
  };

export const forceAsyncFomValidation = () => async (dispatch, getState) => {
  const state = getState();
  const values = getFormValues(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM)(state);

  try {
    await asyncValidate(values);
  } catch (errors) {
    dispatch(
      stopAsyncValidation(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM, errors)
    );
  }
};

export const asyncValidate = async values => {
  const errors = {};

  if (values.signature) {
    const error = await validatePaperlessImageFiles(values.signature);

    if (error) {
      errors.signature = error;
    }
  }

  if (values.logo) {
    const error = await validatePaperlessImageFiles(values.logo);

    if (error) {
      errors.logo = error;
    }
  }

  if (Object.keys(errors).length > 0) {
    throw errors;
  }
};

export const validatePaperlessImageFiles = async files => {
  const file = files[0];

  if (file && !file.originName) {
    return validateImageFile(file);
  }
};

const validatePaperlessForm = async form => {
  let errors = {};

  if (form.has("signatureImage")) {
    const signatureImage = form.get("signatureImage");
    const error = await validateImageFile(signatureImage);

    if (error) {
      errors[F.PAPERLESS_SETTINGS.SIGNATURE] = error;
    }
  }

  if (form.has("logoImage")) {
    const logoImage = form.get("logoImage");
    const error = await validateImageFile(logoImage);

    if (error) {
      errors[F.PAPERLESS_SETTINGS.LOGO] = error;
    }
  }

  if (Object.keys(errors).length > 0) {
    throw new SubmissionError(errors);
  }
};

const createFormDataByFormValues = data => {
  const formData = new FormData();

  formData.append("account", data.account);
  formData.append("allAccounts", data.allAccounts);
  formData.append("printedName", data.name);
  formData.append("title", data.title);

  if (data.signature) {
    if (data.signature[0]?.size) {
      formData.append("signatureImage", data.signature[0]);
    } else {
      formData.append("signatureImageKey", data.signatureImageKey);
    }
  }
  if (data.logo) {
    if (data.logo[0]?.size) {
      formData.append("logoImage", data.logo[0]);
    } else {
      formData.append("logoImageKey", data.logoImageKey);
    }
  }

  return formData;
};

export const savePaperlessSettings = createAsyncAction(
  data => async dispatch => {
    try {
      const formData = createFormDataByFormValues(data);

      await validatePaperlessForm(formData);
      return await dispatch(postPaperlessTradeAccountSettings(formData));
    } catch (paperworkError) {
      if (Array.isArray(paperworkError?.errors)) {
        const paperworkFields = {
          logoImage: F.PAPERLESS_SETTINGS.LOGO,
          signatureImage: F.PAPERLESS_SETTINGS.SIGNATURE,
        };
        const errors = paperworkError?.errors.reduce((acc, error) => {
          if (paperworkFields[error.fieldName]) {
            acc[paperworkFields[error.fieldName]] = error.message;
          }
          return acc;
        }, {});

        if (Object.keys(errors).length > 0) {
          throw new SubmissionError(errors);
        }
      }
      throw paperworkError;
    }
  },
  PaperlessActionTypes.SAVE_PAPERLESS_SETTINGS
);

export const applyAllAccounts = () => (dispatch, getState) => {
  const state = getState();
  const values = getFormValues(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM)(state);
  const initValues = getFormInitialValues(
    F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM
  )(state);
  const valuesChanges = { agree: true };

  for (let field in values) {
    if (values[field] !== initValues[field]) {
      valuesChanges[field] = values[field];
    }
  }

  const touchedField = [];

  if (valuesChanges.signature?.length) {
    if (!valuesChanges.signature[0].size) {
      delete valuesChanges.signature;
    } else {
      touchedField.push("signature");
    }
  }

  if (valuesChanges.logo?.length) {
    if (!valuesChanges.logo[0].size) {
      delete valuesChanges.logo;
    } else {
      touchedField.push("logo");
    }
  }

  dispatch(
    initialize(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM, valuesChanges, true, {
      keepDirty: true,
    })
  );
  if (touchedField.length) {
    dispatch(touch(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM, ...touchedField));
  }
  dispatch(forceAsyncFomValidation());
};

export const disagreeTermsAndConditions = createAsyncAction(
  () => async dispatch => {
    await dispatch(setPaperlessTradeOptionsOut());
    dispatch(initialize(F.FORMS_NAMES.PAPERLESS_SETTINGS_FORM, {}, false));
  },
  PaperlessActionTypes.DISAGREE_TERMS_AND_CONDITIONS
);

export const agreeTermsAndConditions = () => async (dispatch, getState) => {
  const state = getState();
  const accounts = PaperlessSelectors.getPaperlessAccounts(state);

  if (accounts.length === 1) {
    const { accountNumber } = accounts[0];

    await dispatch(changeAccount(accountNumber));
  }
};
